import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { QRCodeCanvas } from 'qrcode.react';
import { getPaynowQRCode } from '../utils/paynowQr';
import { supabase } from '../utils/supabaseClient';

export const PAYNOW_PURPLE = '#901880';
export const PAYNOW_QR_BG = '#FFFFFF';
export const PAYNOW_LOGO_SRC = '/paynow-logo.png';

const MISSING_UEN_MESSAGE =
    'Organization UEN is not set. Please contact the school to complete payment setup.';

/**
 * Load org PayNow payee details from organization table (tenant-specific).
 * No silent Edusphere fallback — empty UEN must be fixed in Organization settings.
 */
export function useOrganizationPayNowDetails() {
    const [details, setDetails] = useState({
        uen: '',
        name: '',
        loading: true,
        error: '',
    });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const { data, error } = await supabase
                    .from('organization')
                    .select('uen, name')
                    .eq('status', 'active')
                    .limit(1)
                    .maybeSingle();

                if (error) throw error;
                if (cancelled) return;

                const uen = data?.uen?.trim() || '';
                const name = data?.name?.trim() || '';
                setDetails({
                    uen,
                    name,
                    loading: false,
                    error: uen ? '' : MISSING_UEN_MESSAGE,
                });
            } catch (err) {
                console.error('Error loading organization PayNow details:', err);
                if (!cancelled) {
                    setDetails({
                        uen: '',
                        name: '',
                        loading: false,
                        error: 'Could not load organization UEN. Please try again.',
                    });
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    return details;
}

function formatAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const n = parseFloat(amount);
    if (Number.isNaN(n) || n <= 0) return null;
    return n.toFixed(2);
}

function sanitizeReference(referenceNumber) {
    if (!referenceNumber) return '';
    return String(referenceNumber)
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 25);
}

/**
 * Live PayNow QR (purple + PayNow logo), matching /qr-testing styling.
 * UEN comes from organization table (or explicit uen prop) — never a hardcoded client.
 */
export function PayNowQRCode({
    amount,
    referenceNumber,
    uen: uenProp,
    size = 180,
    showDownload = false,
    downloadFileName,
    downloadLabel = 'Download QR Code',
    downloadButtonProps = {},
}) {
    const org = useOrganizationPayNowDetails();
    const uen = (uenProp || org.uen || '').trim();
    const qrCanvasRef = useRef(null);
    const [qrValue, setQrValue] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        try {
            setError('');

            if (org.loading && !uenProp) {
                setQrValue('');
                return;
            }

            if (!uen) {
                setError(org.error || MISSING_UEN_MESSAGE);
                setQrValue('');
                return;
            }

            const config = { uen };
            const formattedAmount = formatAmount(amount);
            if (formattedAmount) config.amount = formattedAmount;

            const ref = sanitizeReference(referenceNumber);
            if (ref) config.referenceNumber = ref;

            setQrValue(getPaynowQRCode(config));
        } catch (err) {
            console.error('Error generating PayNow QR:', err);
            setError(err.message || 'Could not generate QR');
            setQrValue('');
        }
    }, [uen, amount, referenceNumber, org.loading, org.error, uenProp]);

    const handleDownload = () => {
        const canvas = qrCanvasRef.current;
        if (!canvas) return;

        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download =
            downloadFileName ||
            `PayNow-QR-${sanitizeReference(referenceNumber) || 'payment'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) {
        return (
            <Text fontSize="xs" color="red.500" textAlign="center" maxW={`${size}px`}>
                {error}
            </Text>
        );
    }

    if (!qrValue) {
        return (
            <Box
                w={`${size}px`}
                h={`${size}px`}
                bg="gray.100"
                borderRadius="md"
            />
        );
    }

    const logoWidth = Math.round(size * 0.25);
    const logoHeight = Math.round(logoWidth * (38 / 56));

    return (
        <VStack spacing={2} align="center">
            <Box bg="white" p={2} borderRadius="md" boxShadow="sm">
                <QRCodeCanvas
                    ref={qrCanvasRef}
                    value={qrValue}
                    size={size}
                    level="H"
                    includeMargin={true}
                    fgColor={PAYNOW_PURPLE}
                    bgColor={PAYNOW_QR_BG}
                    imageSettings={{
                        src: PAYNOW_LOGO_SRC,
                        width: logoWidth,
                        height: logoHeight,
                        excavate: true,
                    }}
                />
            </Box>
            {showDownload && (
                <Button
                    size="sm"
                    variant="outline"
                    colorScheme="green"
                    onClick={handleDownload}
                    {...downloadButtonProps}
                >
                    {downloadLabel}
                </Button>
            )}
        </VStack>
    );
}

export default PayNowQRCode;
